import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { format, startOfWeek, addDays, addWeeks, subWeeks } from "date-fns";
import {
  CalendarDays, ChevronLeft, ChevronRight, Plus, Trash2, Printer, Save,
  Copy, ClipboardPaste, X as XIcon,
  Undo2, Redo2, Eraser,
} from "lucide-react";
import { Card, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { toast } from "sonner";
import PrintHeader from "@/components/PrintHeader";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Sun=0..Sat=6, matching the backend convention.
const DAY_NAMES_FULL = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const WEEK_START_OPTIONS = [
  { value: 0, label: "Sunday" },
  { value: 1, label: "Monday" },
  { value: 2, label: "Tuesday" },
  { value: 3, label: "Wednesday" },
  { value: 4, label: "Thursday" },
  { value: 5, label: "Friday" },
  { value: 6, label: "Saturday" },
];

// Curated row tints that match the reference image
const ROW_COLORS = [
  "#ffffff", "#a4b1cc", "#3b8767", "#f4c896", "#9c9c9c",
  "#c134a7", "#92c83e", "#6cd4ec", "#f4a82e", "#cee4ee",
  "#3370b5", "#ffd7d7", "#f7e6a8",
];

// 15-minute time options used to populate the schedule input <datalist>.
// Chronological order: 12:00 AM → 11:45 PM. Users can still type freely (e.g.
// "2 PM" or "11:15 PM"); the datalist just adds a quick-pick dropdown.
const TIME_OPTIONS = (() => {
  const out = [];
  for (let h = 0; h < 24; h++) {
    for (let m = 0; m < 60; m += 15) {
      const h12 = ((h + 11) % 12) + 1;
      const ampm = h < 12 ? "AM" : "PM";
      out.push(`${h12}:${m.toString().padStart(2, "0")} ${ampm}`);
    }
  }
  return out;
})();

// Snap any date to the first day of its week per the user's preference.
// startOfWeek's weekStartsOn uses the same Sun=0..Sat=6 convention.
const weekStartOf = (d, wsd) => startOfWeek(d, { weekStartsOn: wsd });
const toISODate = (d) => format(d, "yyyy-MM-dd");

// Day labels rotated so the user's start day is first.
const dayLabels = (wsd) =>
  Array.from({ length: 7 }, (_, i) => DAY_NAMES_FULL[(wsd + i) % 7]);

/** Parse "4:00 AM" or "11:15 PM" → minutes since midnight (0..1439). Returns null if blank/invalid. */
function parseTimeMinutes(s) {
  if (!s) return null;
  const m = String(s).trim().match(/^(\d{1,2}):?(\d{2})?\s*(AM|PM|am|pm)?$/);
  if (!m) return null;
  let h = parseInt(m[1], 10);
  const mins = parseInt(m[2] || "0", 10);
  const ap = (m[3] || "").toUpperCase();
  if (Number.isNaN(h) || h < 0 || h > 23 || mins < 0 || mins > 59) return null;
  if (ap === "AM") { if (h === 12) h = 0; }
  else if (ap === "PM") { if (h !== 12) h += 12; }
  return h * 60 + mins;
}

/** Shift hours (handles overnight crossover). Returns hours float. */
function shiftHours(start, end) {
  const s = parseTimeMinutes(start);
  const e = parseTimeMinutes(end);
  if (s == null || e == null) return 0;
  let diff = e - s;
  if (diff < 0) diff += 24 * 60; // overnight wrap
  return diff / 60;
}

/** Format hours as HH:MM */
function fmtHM(hours) {
  if (!hours || Number.isNaN(hours)) return "0:00";
  const total = Math.round(hours * 60);
  const h = Math.floor(total / 60);
  const m = total % 60;
  return `${h}:${String(m).padStart(2, "0")}`;
}

export default function Schedule() {
  // weekStartDay: 0=Sun..6=Sat. Loaded from user settings. Affects which day
  // of the week the schedule starts on (and which column is "first").
  const [weekStartDay, setWeekStartDay] = useState(1); // optimistic Monday default
  const [weekStartDayLoaded, setWeekStartDayLoaded] = useState(false);
  const [weekStart, setWeekStart] = useState(() => startOfWeek(new Date(), { weekStartsOn: 1 }));
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [calOpen, setCalOpen] = useState(false);
  const dirtyTimer = useRef(null);
  const wrapperRef = useRef(null);

  // Day labels rotated so the user's start day is the first column.
  const DAYS = useMemo(() => dayLabels(weekStartDay), [weekStartDay]);

  // Convenience: snap a date to the user's preferred week-start day.
  const snapWeekStart = useCallback((d) => weekStartOf(d, weekStartDay), [weekStartDay]);

  // Load user's week_start_day preference once on mount.
  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/user/settings`).then((res) => {
      if (cancelled) return;
      const wsd = res.data?.week_start_day;
      if (typeof wsd === "number" && wsd >= 0 && wsd <= 6) {
        setWeekStartDay(wsd);
        // Re-snap current weekStart to the new convention.
        setWeekStart((cur) => weekStartOf(cur || new Date(), wsd));
      }
      setWeekStartDayLoaded(true);
    }).catch(() => setWeekStartDayLoaded(true));
    return () => { cancelled = true; };
  }, []);

  // Persist a new week-start-day preference and re-snap the visible week.
  const handleWeekStartDayChange = useCallback(async (newWsd) => {
    setWeekStartDay(newWsd);
    setWeekStart((cur) => weekStartOf(cur || new Date(), newWsd));
    try {
      await axios.put(`${API}/user/settings`, { week_start_day: newWsd });
      toast.success(`Week now starts on ${WEEK_START_OPTIONS[newWsd].label}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save week-start preference");
    }
  }, []);

  // Schedule uses a fixed print layout (see @media print in the JSX below):
  // - width: 7.9in, max-height: 10.5in with overflow: hidden
  // - fixed column widths that sum to ≈ 7.86in
  // - compact row height (44px) sized to the shift-time content
  // With this in place we no longer need a dynamic transform-scale — the page
  // simply fits, and any overflow beyond ~18 employees is clipped rather than
  // spilling to a 2nd page (matches the user's "one page only" requirement).
  useEffect(() => {
    const noop = () => {};
    window.addEventListener('beforeprint', noop);
    window.addEventListener('afterprint', noop);
    return () => {
      window.removeEventListener('beforeprint', noop);
      window.removeEventListener('afterprint', noop);
    };
  }, []);

  const weekDates = useMemo(() => DAYS.map((_, i) => addDays(weekStart, i)), [weekStart]);
  const weekIso = toISODate(weekStart);

  const fetchSchedule = useCallback(async (iso) => {
    try {
      const res = await axios.get(`${API}/schedule/${iso}`);
      setData(res.data);
      // Surface the carry-forward to the user so they know to verify/edit.
      if (res.data?.seeded_from_prior) {
        const from = res.data.seeded_from_week;
        toast.success(
          from
            ? `Carried forward last week's schedule (${from}). Edit as needed.`
            : "Carried forward last week's schedule. Edit as needed."
        );
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load schedule");
    }
  }, []);

  useEffect(() => {
    // Wait until we've loaded the user's week-start preference so we fetch
    // the correctly-snapped week on first paint.
    if (!weekStartDayLoaded) return;
    fetchSchedule(weekIso);
  }, [weekIso, fetchSchedule, weekStartDayLoaded]);

  const persist = useCallback(async (next) => {
    setSaving(true);
    try {
      await axios.put(`${API}/schedule/${weekIso}`, next);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  }, [weekIso]);

  // Debounced auto-save (1s after any change)
  const scheduleAutoSave = useCallback((next) => {
    if (dirtyTimer.current) clearTimeout(dirtyTimer.current);
    dirtyTimer.current = setTimeout(() => persist(next), 1000);
  }, [persist]);

  // ──────────────────────────────────────────────────────────────────────────
  // Undo / Redo
  //
  // We keep up to 50 prior snapshots of `data` in `historyRef`. Each user
  // action that mutates `data` first pushes the PREVIOUS state, so undo can
  // restore it. Redo replays the inverse. Refs avoid re-render churn; a
  // monotonic tick triggers a render only when stack lengths change (so the
  // Undo/Redo buttons can enable/disable correctly).
  // ──────────────────────────────────────────────────────────────────────────
  const historyRef = useRef([]);
  const redoRef = useRef([]);
  const [, setHistoryTick] = useState(0);
  const bumpHistoryTick = useCallback(() => setHistoryTick((t) => (t + 1) % 1e9), []);
  const cloneData = (d) => JSON.parse(JSON.stringify(d));

  const pushHistory = useCallback((state) => {
    if (!state) return;
    historyRef.current = [...historyRef.current, cloneData(state)].slice(-50);
    redoRef.current = []; // any new action clears redo
    bumpHistoryTick();
  }, [bumpHistoryTick]);

  // Wrapped setData that also records the previous snapshot for undo.
  const setDataWithHistory = useCallback((updater) => {
    setData((prev) => {
      if (prev) pushHistory(prev);
      return typeof updater === "function" ? updater(prev) : updater;
    });
  }, [pushHistory]);

  const undo = useCallback(() => {
    if (historyRef.current.length === 0) return;
    const prev = historyRef.current[historyRef.current.length - 1];
    historyRef.current = historyRef.current.slice(0, -1);
    setData((cur) => {
      if (cur) redoRef.current = [...redoRef.current, cloneData(cur)];
      return prev;
    });
    scheduleAutoSave(prev);
    bumpHistoryTick();
  }, [scheduleAutoSave, bumpHistoryTick]);

  const redo = useCallback(() => {
    if (redoRef.current.length === 0) return;
    const next = redoRef.current[redoRef.current.length - 1];
    redoRef.current = redoRef.current.slice(0, -1);
    setData((cur) => {
      if (cur) historyRef.current = [...historyRef.current, cloneData(cur)];
      return next;
    });
    scheduleAutoSave(next);
    bumpHistoryTick();
  }, [scheduleAutoSave, bumpHistoryTick]);

  // Clear ALL shift times for the current week (keeps employee names, phones,
  // colors, and target hours). Useful when starting fresh after carry-forward.
  const clearShifts = useCallback(() => {
    if (!data) return;
    if (!window.confirm("Clear all shift times for this week? Employee names will be kept.")) return;
    setDataWithHistory((prev) => {
      const next = {
        ...prev,
        employees: prev.employees.map((e) => ({
          ...e,
          shifts: e.shifts.map(() => ({ start: "", end: "" })),
        })),
      };
      scheduleAutoSave(next);
      return next;
    });
    toast.success("Cleared all shifts for this week");
  }, [data, setDataWithHistory, scheduleAutoSave]);

  // Reset history whenever we land on a fresh week (so undo doesn't bleed
  // across week boundaries). Also wire keyboard shortcuts globally.
  useEffect(() => {
    historyRef.current = [];
    redoRef.current = [];
    bumpHistoryTick();
  }, [weekIso, bumpHistoryTick]);

  useEffect(() => {
    const onKey = (e) => {
      const isMod = e.ctrlKey || e.metaKey;
      if (!isMod) return;
      const k = e.key.toLowerCase();
      if (k === "z" && !e.shiftKey) {
        // Skip if user is typing in an input that handles its own undo
        const t = e.target;
        if (t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA")) {
          // Only intercept if Schedule has history; otherwise let the browser
          // handle the input's native undo.
          if (historyRef.current.length === 0) return;
        }
        e.preventDefault();
        undo();
      } else if ((k === "z" && e.shiftKey) || k === "y") {
        e.preventDefault();
        redo();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [undo, redo]);

  const updateEmp = (idx, patch) => {
    setDataWithHistory(prev => {
      const next = { ...prev, employees: prev.employees.map((e, i) => i === idx ? { ...e, ...patch } : e) };
      scheduleAutoSave(next);
      return next;
    });
  };

  const updateShift = (empIdx, dayIdx, field, val) => {
    setDataWithHistory(prev => {
      const emps = prev.employees.map((e, i) => {
        if (i !== empIdx) return e;
        const shifts = e.shifts.map((s, j) => j === dayIdx ? { ...s, [field]: val } : s);
        return { ...e, shifts };
      });
      const next = { ...prev, employees: emps };
      scheduleAutoSave(next);
      return next;
    });
  };

  // ──────────────────────────────────────────────────────────────────────────
  // Multi-cell + row copy/paste
  //
  // selectedCells:   Set of "row-col" keys identifying selected shift cells
  // selAnchor:       The first cell clicked; used to compute shift+click ranges
  // cellClipboard:   { topRow, leftCol, cells: [{r,c,start,end}], rowSpan, colSpan }
  // rowClipboard:    A single array of 7 shifts copied via the per-row button
  // ──────────────────────────────────────────────────────────────────────────
  const [selectedCells, setSelectedCells] = useState(new Set());
  const [selAnchor, setSelAnchor] = useState(null);
  const [cellClipboard, setCellClipboard] = useState(null);
  const [rowClipboard, setRowClipboard] = useState(null);

  const keyOf = (r, c) => `${r}-${c}`;

  const clearSelection = useCallback(() => {
    setSelectedCells(new Set());
    setSelAnchor(null);
  }, []);

  // Range-select a rectangle of cells between two corners (inclusive).
  const selectRange = useCallback((r1, c1, r2, c2) => {
    const rMin = Math.min(r1, r2), rMax = Math.max(r1, r2);
    const cMin = Math.min(c1, c2), cMax = Math.max(c1, c2);
    const next = new Set();
    for (let r = rMin; r <= rMax; r++) {
      for (let c = cMin; c <= cMax; c++) next.add(keyOf(r, c));
    }
    setSelectedCells(next);
  }, []);

  // Handle modifier-clicks on a shift cell. Plain clicks fall through to the
  // input so the user can type normally; modifiers toggle selection instead.
  const handleCellMouseDown = (e, empIdx, dayIdx) => {
    if (e.shiftKey) {
      e.preventDefault();
      const anchor = selAnchor || { r: empIdx, c: dayIdx };
      if (!selAnchor) setSelAnchor(anchor);
      selectRange(anchor.r, anchor.c, empIdx, dayIdx);
    } else if (e.ctrlKey || e.metaKey) {
      e.preventDefault();
      const k = keyOf(empIdx, dayIdx);
      setSelectedCells((prev) => {
        const next = new Set(prev);
        if (next.has(k)) next.delete(k); else next.add(k);
        return next;
      });
      setSelAnchor({ r: empIdx, c: dayIdx });
    } else if (selectedCells.size > 0) {
      // Plain click on a shift cell while a selection exists: clear the
      // selection and set this cell as the new anchor for the next paste.
      // Don't preventDefault — input still focuses normally for typing.
      clearSelection();
      setSelAnchor({ r: empIdx, c: dayIdx });
    } else {
      // Track the focused cell as the latest anchor so Ctrl+V works without
      // an explicit selection.
      setSelAnchor({ r: empIdx, c: dayIdx });
    }
  };

  // Click the row-number cell to select that employee's entire 7-day week.
  // Shift extends from anchor row to this row (all columns); Ctrl/⌘ adds.
  const handleRowHeaderClick = (e, empIdx) => {
    if (!data) return;
    if (e.shiftKey && selAnchor) {
      const rMin = Math.min(selAnchor.r, empIdx);
      const rMax = Math.max(selAnchor.r, empIdx);
      const set = new Set();
      for (let r = rMin; r <= rMax; r++) {
        for (let c = 0; c < 7; c++) set.add(keyOf(r, c));
      }
      setSelectedCells(set);
    } else if (e.ctrlKey || e.metaKey) {
      setSelectedCells((prev) => {
        const next = new Set(prev);
        for (let c = 0; c < 7; c++) next.add(keyOf(empIdx, c));
        return next;
      });
    } else {
      const set = new Set();
      for (let c = 0; c < 7; c++) set.add(keyOf(empIdx, c));
      setSelectedCells(set);
    }
    setSelAnchor({ r: empIdx, c: 0 });
  };

  // Click the day-name in a column header to select that day for every
  // employee (a "select column" gesture). Shift extends column range,
  // Ctrl/⌘ adds the column to existing selection.
  const handleDayHeaderClick = (e, dayIdx) => {
    if (!data) return;
    const N = data.employees.length;
    if (N === 0) return;
    if (e.shiftKey && selAnchor) {
      const cMin = Math.min(selAnchor.c, dayIdx);
      const cMax = Math.max(selAnchor.c, dayIdx);
      const set = new Set();
      for (let r = 0; r < N; r++) {
        for (let c = cMin; c <= cMax; c++) set.add(keyOf(r, c));
      }
      setSelectedCells(set);
    } else if (e.ctrlKey || e.metaKey) {
      setSelectedCells((prev) => {
        const next = new Set(prev);
        for (let r = 0; r < N; r++) next.add(keyOf(r, dayIdx));
        return next;
      });
    } else {
      const set = new Set();
      for (let r = 0; r < N; r++) set.add(keyOf(r, dayIdx));
      setSelectedCells(set);
    }
    setSelAnchor({ r: 0, c: dayIdx });
  };

  // Copy currently selected cells. Stores their relative position so paste
  // can replicate the same rectangular pattern starting from a new anchor.
  const copySelectedCells = useCallback(() => {
    if (!data || selectedCells.size === 0) return false;
    const cells = [];
    let topRow = Infinity, leftCol = Infinity, bottomRow = -1, rightCol = -1;
    for (const k of selectedCells) {
      const [r, c] = k.split("-").map(Number);
      topRow = Math.min(topRow, r); bottomRow = Math.max(bottomRow, r);
      leftCol = Math.min(leftCol, c); rightCol = Math.max(rightCol, c);
      const sh = data.employees[r]?.shifts?.[c] || { start: "", end: "" };
      cells.push({ r, c, start: sh.start, end: sh.end });
    }
    setCellClipboard({
      topRow, leftCol,
      rowSpan: bottomRow - topRow + 1,
      colSpan: rightCol - leftCol + 1,
      cells,
    });
    toast.success(`Copied ${cells.length} ${cells.length === 1 ? "cell" : "cells"}`);
    return true;
  }, [data, selectedCells]);

  // Paste cellClipboard with its top-left positioned at the first selected
  // cell (anchor) — or, if no selection exists, the most-recently clicked
  // cell (selAnchor). This lets users copy a range, plain-click a fresh
  // destination, and Ctrl+V to paste there.
  const pasteAtAnchor = useCallback(() => {
    if (!cellClipboard) {
      toast.error("Nothing to paste — copy some cells first.");
      return false;
    }
    let anchor = null;
    if (selectedCells.size > 0) {
      // Use the smallest selected cell as the paste anchor.
      let minR = Infinity, minC = Infinity;
      for (const k of selectedCells) {
        const [r, c] = k.split("-").map(Number);
        if (r < minR || (r === minR && c < minC)) { minR = r; minC = c; }
      }
      anchor = { r: minR, c: minC };
    } else if (selAnchor) {
      anchor = selAnchor;
    }
    if (!anchor) {
      toast.error("Click a destination cell first.");
      return false;
    }
    setDataWithHistory(prev => {
      if (!prev) return prev;
      const emps = prev.employees.map((e) => ({ ...e, shifts: e.shifts.map((s) => ({ ...s })) }));
      let pasted = 0;
      for (const c of cellClipboard.cells) {
        const dr = c.r - cellClipboard.topRow;
        const dc = c.c - cellClipboard.leftCol;
        const tr = anchor.r + dr;
        const tc = anchor.c + dc;
        if (tr >= 0 && tr < emps.length && tc >= 0 && tc < 7) {
          emps[tr].shifts[tc] = { start: c.start, end: c.end };
          pasted++;
        }
      }
      const next = { ...prev, employees: emps };
      scheduleAutoSave(next);
      toast.success(`Pasted ${pasted} ${pasted === 1 ? "cell" : "cells"}`);
      return next;
    });
    return true;
  }, [cellClipboard, selAnchor, selectedCells, scheduleAutoSave]);

  // Per-row copy / paste — quick way to replicate one employee's whole week
  // onto another. The most common workflow when many employees share hours.
  const copyRow = (empIdx) => {
    const e = data.employees[empIdx];
    if (!e) return;
    setRowClipboard(e.shifts.map((s) => ({ start: s.start, end: s.end })));
    toast.success(`Copied ${e.name || `row ${empIdx + 1}`}'s schedule`);
  };
  const pasteRow = (empIdx) => {
    if (!rowClipboard) { toast.error("Copy a row first."); return; }
    setDataWithHistory(prev => {
      const emps = prev.employees.map((e, i) =>
        i === empIdx ? { ...e, shifts: rowClipboard.map((s) => ({ ...s })) } : e
      );
      const next = { ...prev, employees: emps };
      scheduleAutoSave(next);
      return next;
    });
    toast.success(`Pasted onto row ${empIdx + 1}`);
  };

  // Document-level keyboard shortcuts. Active only while Schedule is mounted.
  useEffect(() => {
    const onKey = (e) => {
      // Skip if user is typing in a non-shift input (let normal copy/paste work).
      const target = e.target;
      const inShiftCell = target && target.closest && target.closest("td.sched-shift");
      const isMod = e.ctrlKey || e.metaKey;
      if (isMod && e.key.toLowerCase() === "c") {
        if (selectedCells.size > 0) {
          e.preventDefault();
          copySelectedCells();
        }
      } else if (isMod && e.key.toLowerCase() === "v") {
        if (cellClipboard && (selectedCells.size > 0 || inShiftCell || selAnchor)) {
          e.preventDefault();
          pasteAtAnchor();
        }
      } else if (e.key === "Escape") {
        if (selectedCells.size > 0) clearSelection();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [selectedCells, cellClipboard, selAnchor, copySelectedCells, pasteAtAnchor, clearSelection]);

  const addEmployee = () => {
    setDataWithHistory(prev => {
      const next = {
        ...prev,
        employees: [
          ...prev.employees,
          { id: crypto.randomUUID(), name: "", phone: "", color: "#ffffff",
            shifts: Array.from({ length: 7 }, () => ({ start: "", end: "" })) },
        ],
      };
      scheduleAutoSave(next);
      return next;
    });
  };

  const removeEmployee = (idx) => {
    setDataWithHistory(prev => {
      const next = { ...prev, employees: prev.employees.filter((_, i) => i !== idx) };
      scheduleAutoSave(next);
      return next;
    });
  };

  const updateTarget = (v) => {
    setDataWithHistory(prev => {
      const next = { ...prev, target_hours: parseInt(v) || 0 };
      scheduleAutoSave(next);
      return next;
    });
  };

  const totals = useMemo(() => {
    if (!data) return { perRow: [], perDay: Array(7).fill(0), grand: 0 };
    const perRow = data.employees.map(e => e.shifts.reduce((sum, s) => sum + shiftHours(s.start, s.end), 0));
    const perDay = Array(7).fill(0);
    data.employees.forEach(e => e.shifts.forEach((s, d) => { perDay[d] += shiftHours(s.start, s.end); }));
    const grand = perDay.reduce((a, b) => a + b, 0);
    return { perRow, perDay, grand };
  }, [data]);

  if (!data) {
    return <div className="text-center text-muted-foreground py-12 font-mono">Loading schedule...</div>;
  }

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-4">
      {/* Controls — hidden when printing */}
      <Card className="border-0 shadow-lg print:hidden">
        <CardHeader className="py-3">
          <div className="flex flex-wrap items-center gap-2 justify-between">
            <CardTitle className="font-mono flex items-center gap-2">
              <CalendarDays className="w-5 h-5 text-primary" />
              Employee Schedule
            </CardTitle>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => setWeekStart(snapWeekStart(subWeeks(weekStart, 1)))} data-testid="schedule-prev-week">
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <Popover open={calOpen} onOpenChange={setCalOpen}>
                <PopoverTrigger asChild>
                  <Button variant="outline" size="sm" className="font-mono" data-testid="schedule-week-picker">
                    {format(weekStart, "MMM d")} – {format(addDays(weekStart, 6), "MMM d, yyyy")}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="center">
                  <Calendar
                    mode="single"
                    selected={weekStart}
                    onSelect={(d) => { if (d) { setWeekStart(snapWeekStart(d)); setCalOpen(false); } }}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
              <Button variant="outline" size="sm" onClick={() => setWeekStart(snapWeekStart(addWeeks(weekStart, 1)))} data-testid="schedule-next-week">
                <ChevronRight className="w-4 h-4" />
              </Button>
              <Button variant="outline" size="sm" onClick={() => setWeekStart(snapWeekStart(new Date()))} data-testid="schedule-this-week">
                This Week
              </Button>
              <select
                value={weekStartDay}
                onChange={(e) => handleWeekStartDayChange(parseInt(e.target.value, 10))}
                className="h-8 rounded-md border border-input bg-background px-2 text-xs font-mono"
                title="Day of the week that starts your schedule"
                data-testid="schedule-week-start-day"
              >
                {WEEK_START_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>Starts {o.label}</option>
                ))}
              </select>
              <Button variant="outline" size="sm" onClick={() => window.print()} className="ml-2" data-testid="schedule-print">
                <Printer className="w-4 h-4 mr-1" /> Print
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={undo}
                disabled={historyRef.current.length === 0}
                title="Undo (Ctrl/⌘+Z)"
                data-testid="schedule-undo"
              >
                <Undo2 className="w-4 h-4" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={redo}
                disabled={redoRef.current.length === 0}
                title="Redo (Ctrl/⌘+Shift+Z or Ctrl+Y)"
                data-testid="schedule-redo"
              >
                <Redo2 className="w-4 h-4" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={clearShifts}
                disabled={!data || data.employees.length === 0}
                title="Clear all shift times for this week (keeps names)"
                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                data-testid="schedule-clear"
              >
                <Eraser className="w-4 h-4 mr-1" /> Clear
              </Button>
              <Button size="sm" onClick={() => persist(data)} disabled={saving} className="btn-primary" data-testid="schedule-save">
                <Save className="w-4 h-4 mr-1" /> {saving ? "Saving..." : "Save"}
              </Button>
            </div>
          </div>
          <p className="text-[11px] text-muted-foreground font-mono mt-1">
            Auto-saves 1 second after each change. Times accept formats like <span className="text-primary">4:00 AM</span>, <span className="text-primary">11:15 PM</span>, or <span className="text-primary">2 PM</span>. Overnight shifts are handled automatically.
          </p>
          <p className="text-[11px] text-muted-foreground font-mono mt-0.5">
            <span className="text-primary font-semibold">Tip:</span>{' '}
            Click the <span className="text-blue-600 font-semibold">row #</span> to select a whole row, or a <span className="text-blue-600 font-semibold">day header</span> to select that day for everyone.{' '}
            <span className="text-blue-600 font-semibold">Shift</span>+click a cell to range-select, <span className="text-blue-600 font-semibold">Ctrl/⌘</span>+click to toggle.{' '}
            Then <span className="text-blue-600 font-semibold">Ctrl/⌘+C</span> to copy and <span className="text-blue-600 font-semibold">Ctrl/⌘+V</span> to paste. Or use the <Copy className="inline-block w-3 h-3 -mt-0.5" /> icon on a row to copy the whole week.
          </p>
        </CardHeader>
      </Card>

      {/* Selection / clipboard status bar — only shown when there's something to act on */}
      {(selectedCells.size > 0 || cellClipboard || rowClipboard) && (
        <div className="flex flex-wrap items-center gap-2 print:hidden px-3 py-2 rounded-md border border-blue-500/30 bg-blue-500/5" data-testid="schedule-selection-bar">
          {selectedCells.size > 0 && (
            <span className="font-mono text-xs text-blue-700">
              <strong>{selectedCells.size}</strong> {selectedCells.size === 1 ? "cell" : "cells"} selected
            </span>
          )}
          {cellClipboard && (
            <span className="font-mono text-xs text-emerald-700">
              Clipboard: <strong>{cellClipboard.cells.length}</strong> {cellClipboard.cells.length === 1 ? "cell" : "cells"}
              {cellClipboard.rowSpan > 1 || cellClipboard.colSpan > 1
                ? ` (${cellClipboard.rowSpan}×${cellClipboard.colSpan})`
                : ""}
            </span>
          )}
          {rowClipboard && (
            <span className="font-mono text-xs text-emerald-700">
              Row clipboard ready
            </span>
          )}
          <div className="ml-auto flex gap-1">
            {selectedCells.size > 0 && (
              <Button variant="outline" size="sm" onClick={copySelectedCells} className="h-7" data-testid="schedule-copy-selection">
                <Copy className="w-3 h-3 mr-1" />
                Copy {selectedCells.size > 1 ? `(${selectedCells.size})` : ''}
              </Button>
            )}
            {cellClipboard && selectedCells.size > 0 && (
              <Button variant="outline" size="sm" onClick={pasteAtAnchor} className="h-7" data-testid="schedule-paste-selection">
                <ClipboardPaste className="w-3 h-3 mr-1" />
                Paste
              </Button>
            )}
            {selectedCells.size > 0 && (
              <Button variant="ghost" size="sm" onClick={clearSelection} className="h-7" data-testid="schedule-clear-selection">
                <XIcon className="w-3 h-3 mr-1" />
                Clear
              </Button>
            )}
          </div>
        </div>
      )}

      {/* Schedule grid */}
      <div className="schedule-print-area">
        <div className="schedule-wrapper" ref={wrapperRef}>
          <PrintHeader />
          {/* Header bar */}
          <div className="schedule-title">
            <span>SCHEDULE</span>
            <div className="schedule-target print:hidden">
              <label className="text-[10px] font-bold mr-1">Target:</label>
              <Input
                value={data.target_hours}
                onChange={(e) => updateTarget(e.target.value)}
                className="h-6 w-14 text-center font-mono text-xs bg-white text-black"
                data-testid="schedule-target"
              />
            </div>
            <div className="schedule-target hidden print:flex">{data.target_hours}</div>
          </div>

          {/* Shared 15-minute time-picker dropdown — every shift input references
              this list so users can click to open a native time picker, or just
              type a custom time. */}
          <datalist id="schedule-time-list">
            {TIME_OPTIONS.map((t) => (
              <option key={t} value={t} />
            ))}
          </datalist>

          <table className="schedule-table">
            <colgroup>
              <col className="col-no" style={{ width: "38px" }} />
              <col className="col-name" style={{ width: "200px" }} />
              {DAYS.map((d) => <col key={d} className="col-day" style={{ width: "115px" }} />)}
              <col className="col-total" style={{ width: "80px" }} />
            </colgroup>
            <thead>
              <tr className="schedule-header-row">
                <th rowSpan={2} className="sched-th-no">No</th>
                <th rowSpan={2} className="sched-th-name"><div>Date</div><div className="mt-2">Names</div></th>
                {weekDates.map((d, i) => (
                  <th key={i} className="sched-th-day">
                    {i === 0 ? (
                      <input
                        type="date"
                        value={toISODate(d)}
                        onChange={(e) => {
                          if (!e.target.value) return;
                          // Parse local date and snap to the user's preferred week-start day
                          const [yy, mm, dd] = e.target.value.split('-').map(Number);
                          const picked = new Date(yy, mm - 1, dd);
                          setWeekStart(snapWeekStart(picked));
                        }}
                        className="text-[10px] font-bold w-[100px] text-center border border-amber-400 rounded px-1 py-0.5 bg-amber-50 text-black font-mono cursor-pointer hover:bg-amber-100"
                        title="Pick any date — week snaps to your week-start day. All other day-dates update automatically."
                        data-testid="schedule-first-date-editable"
                      />
                    ) : (
                      <div className="text-[10px] font-normal">{format(d, "M/d/yy")}</div>
                    )}
                    {i === 0 ? (
                      <div className="font-bold mt-0.5 inline-flex items-center justify-center gap-1">
                        <button
                          type="button"
                          onClick={(e) => handleDayHeaderClick(e, i)}
                          className="text-[10px] px-1 py-0 rounded hover:bg-emerald-700 hover:text-white opacity-70 hover:opacity-100"
                          title="Select all employees on this day · Shift to extend · Ctrl/⌘ to add"
                          data-testid={`day-header-select-${i}`}
                        >
                          ▾col
                        </button>
                        <select
                          value={weekStartDay}
                          onChange={(e) => handleWeekStartDayChange(parseInt(e.target.value, 10))}
                          className="bg-transparent text-center cursor-pointer border-0 outline-none hover:underline focus:underline appearance-none print:appearance-none print:bg-transparent"
                          style={{
                            backgroundImage: 'url("data:image/svg+xml;charset=US-ASCII,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' viewBox=\'0 0 4 5\'%3E%3Cpath fill=\'%23444\' d=\'M2 0L0 2h4zm0 5L0 3h4z\'/%3E%3C/svg%3E")',
                            backgroundRepeat: 'no-repeat',
                            backgroundPosition: 'right 0.25rem center',
                            backgroundSize: '0.5rem',
                            paddingRight: '0.85rem',
                            paddingLeft: '0.25rem',
                          }}
                          title="Click to change the day your week starts on"
                          data-testid="schedule-first-day-inline"
                        >
                          {WEEK_START_OPTIONS.map((o) => (
                            <option key={o.value} value={o.value}>
                              {DAY_NAMES_FULL[o.value]}
                            </option>
                          ))}
                        </select>
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={(e) => handleDayHeaderClick(e, i)}
                        className="font-bold mt-0.5 w-full bg-transparent border-0 cursor-pointer hover:bg-emerald-700 hover:text-white rounded"
                        title="Select all employees on this day · Shift to extend · Ctrl/⌘ to add"
                        data-testid={`day-header-select-${i}`}
                      >
                        {DAYS[i]}
                      </button>
                    )}
                  </th>
                ))}
                <th rowSpan={2} className="sched-th-total">Total</th>
              </tr>
            </thead>
            <tbody>
              {data.employees.map((emp, idx) => (
                <tr key={emp.id} className="schedule-row">
                  <td
                    className="sched-no sched-no-clickable"
                    onClick={(e) => handleRowHeaderClick(e, idx)}
                    title="Click to select the entire row · Shift to extend · Ctrl/⌘ to add"
                    data-testid={`row-header-${idx}`}
                  >
                    {idx + 1}
                  </td>
                  <td className="sched-name" style={{ backgroundColor: emp.color || "#ffffff" }}>
                    <Input
                      value={emp.name}
                      onChange={(e) => updateEmp(idx, { name: e.target.value })}
                      placeholder="Name"
                      className="h-10 !text-lg font-mono font-bold bg-transparent border-0 p-1 shadow-none focus-visible:ring-1"
                      data-testid={`emp-name-${idx}`}
                    />
                    <Input
                      value={emp.phone}
                      onChange={(e) => updateEmp(idx, { phone: e.target.value })}
                      placeholder="Phone"
                      className="h-7 !text-xs font-mono bg-transparent border-0 p-1 mt-0.5 shadow-none focus-visible:ring-1"
                      data-testid={`emp-phone-${idx}`}
                    />
                    {/* Color + copy/paste/delete (hidden on print) */}
                    <div className="flex gap-1 mt-1 print:hidden items-center">
                      {ROW_COLORS.map(c => (
                        <button
                          key={c}
                          type="button"
                          onClick={() => updateEmp(idx, { color: c })}
                          className={`w-3 h-3 rounded-sm border ${emp.color === c ? "ring-1 ring-black" : "border-black/20"}`}
                          style={{ backgroundColor: c }}
                          title="Set row color"
                          data-testid={`emp-color-${idx}-${c}`}
                        />
                      ))}
                      <button
                        type="button"
                        onClick={() => copyRow(idx)}
                        className="ml-auto text-blue-600 hover:text-blue-700 p-0.5 rounded hover:bg-blue-50"
                        title="Copy this row's schedule"
                        data-testid={`emp-copy-row-${idx}`}
                      >
                        <Copy className="w-3 h-3" />
                      </button>
                      {rowClipboard && (
                        <button
                          type="button"
                          onClick={() => pasteRow(idx)}
                          className="text-emerald-600 hover:text-emerald-700 p-0.5 rounded hover:bg-emerald-50"
                          title="Paste copied schedule onto this row"
                          data-testid={`emp-paste-row-${idx}`}
                        >
                          <ClipboardPaste className="w-3 h-3" />
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => removeEmployee(idx)}
                        className="text-red-600 hover:text-red-700 p-0.5 rounded hover:bg-red-50"
                        title="Remove employee"
                        data-testid={`emp-remove-${idx}`}
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  </td>
                  {DAYS.map((_, d) => {
                    const sh = emp.shifts[d] || { start: "", end: "" };
                    const isSelected = selectedCells.has(keyOf(idx, d));
                    return (
                      <td
                        key={d}
                        className={`sched-shift ${isSelected ? "sched-cell-selected" : ""}`}
                        onMouseDown={(e) => handleCellMouseDown(e, idx, d)}
                        data-testid={`cell-${idx}-${d}`}
                      >
                        <Input
                          value={sh.start}
                          onChange={(e) => updateShift(idx, d, "start", e.target.value)}
                          list="schedule-time-list"
                          className="h-9 text-center !text-base font-mono font-semibold p-0 border border-amber-300 bg-amber-100 text-black"
                          placeholder=""
                          data-testid={`emp-${idx}-day-${d}-start`}
                        />
                        <Input
                          value={sh.end}
                          onChange={(e) => updateShift(idx, d, "end", e.target.value)}
                          list="schedule-time-list"
                          className="h-9 text-center !text-base font-mono font-semibold p-0 mt-0.5 border border-orange-300 bg-orange-100 text-black"
                          placeholder=""
                          data-testid={`emp-${idx}-day-${d}-end`}
                        />
                      </td>
                    );
                  })}
                  <td className={`sched-row-total ${totals.perRow[idx] >= 40 ? "over" : ""}`}>
                    {fmtHM(totals.perRow[idx])}
                  </td>
                </tr>
              ))}
              {/* Total row */}
              <tr className="schedule-bottom-totals">
                <td colSpan={2} className="text-right pr-2 font-bold text-red-600 print:text-red-700">Total</td>
                {totals.perDay.map((h, i) => (
                  <td key={i} className="font-bold text-red-600 text-center">{fmtHM(h)}</td>
                ))}
                <td className={`font-bold text-center ${totals.grand > data.target_hours ? "text-red-600" : "text-emerald-700"}`}>
                  {fmtHM(totals.grand)}
                </td>
              </tr>
            </tbody>
          </table>

          {/* Add employee */}
          <div className="mt-3 flex justify-between items-center print:hidden">
            <Button variant="outline" size="sm" onClick={addEmployee} className="font-mono" data-testid="schedule-add-emp">
              <Plus className="w-4 h-4 mr-1" /> Add Employee
            </Button>
            <div className="text-xs font-mono text-muted-foreground">
              Week total: <span className={totals.grand > data.target_hours ? "text-red-500 font-bold" : "text-primary font-bold"}>{fmtHM(totals.grand)}</span>
              {" / "}
              Target: <span className="font-bold">{data.target_hours}</span>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .schedule-wrapper {
          background: white;
          color: black;
          padding: 6px 4px;
          border-radius: 8px;
          box-shadow: 0 4px 14px rgba(0,0,0,0.2);
          overflow-x: auto;
        }
        .schedule-title {
          background: hsl(148, 80%, 18%);
          color: #34ff5e;
          font-family: 'Courier New', monospace;
          font-weight: 900;
          font-size: 28px;
          text-align: center;
          padding: 8px 12px;
          position: relative;
          letter-spacing: 4px;
          text-shadow: 0 2px 0 #064f1a;
          border-radius: 6px 6px 0 0;
        }
        .schedule-target {
          position: absolute;
          right: 14px; top: 10px;
          font-family: 'Courier New', monospace;
          color: #34ff5e;
          font-size: 14px;
          font-weight: 700;
          align-items: center; gap: 4px;
        }
        /* Only show as flex when not explicitly hidden via Tailwind .hidden */
        .schedule-target:not(.hidden) { display: flex; }
        /* The Target input must show plain black text — kill the inherited green
           text-shadow / color cascading from the parent .schedule-title. */
        .schedule-target input {
          color: #000 !important;
          text-shadow: none !important;
          background: #ffffff !important;
          -webkit-text-fill-color: #000 !important;
        }
        .schedule-target label {
          text-shadow: none !important;
        }
        .schedule-table {
          width: 100%;
          border-collapse: collapse;
          font-family: 'Courier New', monospace;
          background: white;
          color: black;
        }
        .schedule-table th, .schedule-table td {
          border: 1px solid #1d4ed8;
          padding: 0;
          vertical-align: middle;
        }
        .schedule-header-row th { background: #f8e7c4; color: black; padding: 6px 3px; font-size: 14px; text-align: center; font-weight: 700; }
        .sched-th-no { width: 44px; }
        .sched-th-name { background: #fbe5b5 !important; text-align: center; }
        .sched-th-day { background: #fbe5b5 !important; text-align: center; }
        .sched-th-total { background: hsl(148, 50%, 35%) !important; color: white !important; text-align: center; font-weight: 800; font-size: 15px; }
        .sched-no { background: #f8c997; text-align: center; font-weight: 700; font-size: 16px; padding: 6px 0; color: black; }
        .sched-no-clickable { cursor: pointer; user-select: none; }
        .sched-no-clickable:hover { background: #e0a468; }
        .sched-name { padding: 4px 6px; min-width: 210px; color: black; }
        .sched-shift { padding: 4px; background: white; position: relative; }
        .sched-shift input { background: #fff8b0; border-radius: 2px; }
        .sched-shift input:nth-of-type(2) { background: #ffe1c4; }
        .sched-cell-selected {
          outline: 2px solid #2563eb;
          outline-offset: -2px;
          background-color: rgba(37, 99, 235, 0.10) !important;
        }
        .sched-row-total { background: #c9c9c9; color: black; text-align: center; font-weight: 700; font-size: 16px; }
        .sched-row-total.over { color: #c1272d; }
        .schedule-bottom-totals td { background: #f8d09a; padding: 5px; font-family: 'Courier New', monospace; font-size: 15px; }

        @media print {
          @page { size: letter portrait; margin: 0.25in; }
          html, body { background: white !important; }
          nav, header, footer, .print\\:hidden { display: none !important; }

          /* Force browsers to print backgrounds AND border colors so employees
             appear in cleanly separated rows with solid black borders. */
          * {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
            color-adjust: exact !important;
          }

          .schedule-print-area { padding: 0; display: block; }
          .schedule-wrapper {
            box-shadow: none;
            padding: 0;
            /* Natural height so rows sit at their compact fit-to-content size
               instead of stretching to fill the page. max-height + overflow
               hidden guarantees we never spill onto a 2nd page. */
            display: block;
            width: 7.9in;
            max-height: 10.5in;
            overflow: hidden;
            page-break-inside: avoid;
            break-inside: avoid;
            page-break-after: avoid;
            break-after: avoid;
          }
          .schedule-title { padding: 6px 12px; font-size: 18px; }

          /* Explicit column widths in print so 8 columns fit within 7.9in with
             room for legible fonts. Total: 0.4 + 1.3 + 7×0.88 = 7.86in.
             Inline widths from <colgroup> are overridden with !important. */
          .schedule-table { table-layout: fixed !important; width: 7.9in !important; }
          .schedule-table col.col-no { width: 0.4in !important; }
          .schedule-table col.col-name { width: 1.3in !important; }
          .schedule-table col.col-day { width: 0.88in !important; }
          .schedule-table col.col-total { width: 0 !important; display: none !important; }

          /* Compact table sized to its content — no stretching */
          .schedule-table {
            font-size: 14px;
            border-collapse: collapse;
            border: 2px solid #000 !important;
          }
          /* STRONG BLACK BORDERS on every cell so each employee's row is
             clearly separated on the printed page. */
          .schedule-table th,
          .schedule-table td {
            border: 1.5px solid #000 !important;
          }
          .schedule-header-row th { padding: 4px 3px; font-size: 12px; font-weight: 800; }
          .schedule-bottom-totals td { padding: 4px 4px; font-size: 12px; }

          /* PRIVACY: hide the Total column and Total row in print so employees
             don't see each other's hours. */
          .schedule-bottom-totals { display: none !important; }
          .sched-th-total, .sched-row-total { display: none !important; }

          /* Fit-to-content row height — compact, sized to the shift-time inputs
             (start on top + end on bottom + small gap). Roughly 44-48px each
             so ~18 employees fit comfortably on one letter page. */
          .schedule-row { height: 44px !important; }
          .schedule-row td { vertical-align: middle; padding: 2px 2px !important; height: 44px !important; }
          /* Row-number cell — big bold digit */
          .sched-no { font-size: 18px !important; padding: 2px 0 !important; font-weight: 800 !important; }
          .sched-name { padding: 2px 3px !important; min-width: 0 !important; }
          /* Employee name — bold, legible */
          .sched-name input {
            height: auto !important;
            min-height: 20px !important;
            font-size: 14px !important;
            font-weight: 700 !important;
            padding: 1px 3px !important;
            margin: 0 !important;
            color: #000 !important;
            border: none !important;
            letter-spacing: -0.3px;
          }
          .sched-name input + input {
            font-size: 10px !important;
            font-weight: 500 !important;
            min-height: 14px !important;
            margin-top: 1px !important;
          }
          .sched-shift { padding: 1px 1px !important; height: 44px !important; }
          /* Shift start/end times — sized so BOTH "11:15 PM" and "12:00 PM"
             (all 5-digit + AM/PM formats) render in full without clipping.
             Overriding font-family to proportional (Arial) in print so wide
             monospace characters don't get their trailing 'M' clipped. */
          .sched-shift input {
            height: 18px !important;
            min-height: 18px !important;
            font-family: Arial, 'Helvetica', sans-serif !important;
            font-size: 14px !important;
            font-weight: 700 !important;
            padding: 1px 1px !important;
            color: #000 !important;
            /* Light gray border around each time input — subtle enough to
               keep the printed page from looking like a boxed grid, while
               still separating the start/end times visually. */
            border: 1px solid #cfcfcf !important;
            border-radius: 2px !important;
            letter-spacing: -0.2px;
            text-align: center !important;
            text-overflow: clip !important;
            overflow: visible !important;
            line-height: 1.1 !important;
          }
          .sched-shift > div {
            gap: 2px;
            height: 100%;
            display: flex !important;
            flex-direction: column !important;
            justify-content: center !important;
          }
          .schedule-target { font-size: 13px; right: 10px; top: 6px; }
        }
      `}</style>
    </motion.div>
  );
}
